import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, BookOpen, TrendingUp, TrendingDown, Wallet, AlertCircle } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';
import { formatDate } from '../../utils/dateTimeUtils';

const DayBook = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState({ totalIncome: 0, totalExpense: 0, balance: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchReport(); }, [date]);

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const [incomesRes, expensesRes] = await Promise.all([
        apiClient.get(`/api/accounts/incomes?from_date=${date}&to_date=${date}&limit=10000`),
        apiClient.get(`/api/accounts/expenses?from_date=${date}&to_date=${date}&limit=10000`)
      ]);

      const incomes = Array.isArray(incomesRes.data) ? incomesRes.data : [];
      const expenses = Array.isArray(expensesRes.data) ? expensesRes.data : [];
      
      const totalIncome = incomesRes.pagination?.stats?.total_amount || incomes.reduce((sum, i) => sum + (i.amount || 0), 0);
      const totalExpense = expensesRes.pagination?.stats?.total_amount || expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      
      const combined = [
        ...incomes.map(i => ({ 
          ...i, 
          type: 'Income', 
          date: i.income_date,
          category: i.category || i.income_type || 'Other Income'
        })), 
        ...expenses.map(e => ({ 
          ...e, 
          type: 'Expense', 
          date: e.expense_date,
          category: e.category || e.expense_type || 'Other Expense'
        }))
      ];
      combined.sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date));
      
      setTransactions(combined);
      setSummary({ totalIncome, totalExpense, balance: totalIncome - totalExpense });
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load day book data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Day Book
          </CardTitle>
          <div className="flex gap-4 items-center">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-40" />
            <Button onClick={fetchReport} disabled={loading}>
              <FileText className="w-4 h-4 mr-2" />{loading ? 'Loading...' : 'Generate'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" onClick={fetchReport} className="ml-auto">Retry</Button>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-600">Total Income</p>
              </div>
              <p className="text-2xl font-bold text-green-700">₹{summary.totalIncome.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-600" />
                <p className="text-sm text-red-600">Total Expense</p>
              </div>
              <p className="text-2xl font-bold text-red-700">₹{summary.totalExpense.toLocaleString('en-IN')}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                <p className="text-sm text-blue-600">Net Balance</p>
              </div>
              <p className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>
                ₹{summary.balance.toLocaleString('en-IN')}
              </p>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-2">
            Showing {transactions.length} transactions for {formatDate(date)}
          </div>

          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left p-3">Time</th>
                <th className="text-left p-3">Type</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Description</th>
                <th className="text-right p-3">Amount</th>
                <th className="text-left p-3">Payment Mode</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center p-8 text-gray-500">Loading...</td></tr>
              ) : transactions.length === 0 ? (
                <tr><td colSpan="6" className="text-center p-8 text-gray-500">No transactions found for this date</td></tr>
              ) : (
                transactions.map((txn, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-gray-600">
                      {txn.created_at ? new Date(txn.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${txn.type === 'Income' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {txn.type}
                      </span>
                    </td>
                    <td className="p-3">{txn.category || '-'}</td>
                    <td className="p-3 max-w-xs truncate">{txn.description || txn.remarks || '-'}</td>
                    <td className={`p-3 text-right font-semibold ${txn.type === 'Income' ? 'text-green-600' : 'text-red-600'}`}>
                      {txn.type === 'Income' ? '+' : '-'}₹{(txn.amount || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="p-3">{txn.payment_mode || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DayBook;
